import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../../constants/api';
import { logger } from '../../../helpers/logger.helper';
import { STRIPE_PUBLISHABLE_KEY_GENERATED } from '../../stripe-config.generated';

// CI: scripts/inject-stripe-env.js overwrites the literal; local: usually empty + EXPO_PUBLIC_* in .env.
// Metro on CI does not reliably inline EXPO_PUBLIC_*, so prefer the generated literal and fall back to env.
const generatedKey = String(STRIPE_PUBLISHABLE_KEY_GENERATED ?? '').trim();
const publishableKey = (
  generatedKey || (process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? '')
).trim();

// Lazy + memoized: only call loadStripe when the overlay actually renders AND we have a key.
// Never call loadStripe('') — an empty key throws a Stripe IntegrationError as an unhandled
// promise rejection that fires on every overlay open (the prod web error-rate alert).
let stripePromiseSingleton: ReturnType<typeof loadStripe> | null = null;
function getStripePromise() {
  if (!publishableKey) return null;
  if (!stripePromiseSingleton) {
    stripePromiseSingleton = loadStripe(publishableKey);
  }
  return stripePromiseSingleton;
}

export interface StripeProCheckoutOverlayProps {
  clientSecret: string;
  sessionId: string;
  onComplete: () => void;
  onCancel: () => void;
}

export function StripeProCheckoutOverlay({ clientSecret, sessionId, onComplete, onCancel }: StripeProCheckoutOverlayProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const completedRef = useRef(false);
  const stripePromise = getStripePromise();

  const handleComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      await apiClient(API_ENDPOINTS.billing.consumerProSyncCheckoutSession, {
        method: 'POST',
        data: { sessionId },
      });
    } catch {
      // Non-fatal — webhook will sync asynchronously
    }
    // Invalidate so useIsProUser refetches and shows the new Pro status
    await queryClient.invalidateQueries({ queryKey: ['proStatus'] });
    onComplete();
  }, [apiClient, sessionId, queryClient, onComplete]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (!stripePromise) {
      logger.warn(
        'Stripe publishable key missing — Pro web checkout unavailable. Production builds need EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY (GitHub Actions secret EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY_PROD); inject-stripe-env runs before the bundle.',
      );
    }
  }, [stripePromise]);

  const options = { clientSecret, onComplete: handleComplete };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(0,0,0,0.75)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#111827',
          flexShrink: 0,
        }}
      >
        <span style={{ color: '#F9FAFB', fontSize: 16, fontWeight: 600 }}>Upgrade to Pro</span>
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9CA3AF',
            fontSize: 22,
            lineHeight: 1,
            padding: '4px 8px',
          }}
          aria-label="Close checkout"
        >
          ✕
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#ffffff' }}>
        {stripePromise ? (
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: 32,
              textAlign: 'center',
              color: '#374151',
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 600 }}>Checkout is temporarily unavailable</span>
            <span style={{ fontSize: 14, color: '#6B7280' }}>
              Card payments aren’t available right now. Please try again later.
            </span>
            <button
              onClick={onCancel}
              style={{
                marginTop: 8,
                padding: '10px 20px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: '#111827',
                color: '#F9FAFB',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
